// Article « RSVP mariage » (référencement), page centrale du sujet « réponses des
// invités ». En français : il vise les recherches Google françaises. Les sous-sujets
// ont leur page (QR code, formulaire, Google Form : voir common/rsvpCluster.ts).
// Les fonctionnalités citées sont celles du RSVP de l'app.

import { Template } from '../common/TemplateCard';
import { FaqItem } from '../common/GuideLayout';

export const UPDATED = '2026-10-15';
export const READING_MINUTES = 7;

export const TOC = [
  { id: 'definition', label: 'Que veut dire RSVP sur un faire-part ?' },
  { id: 'en-ligne', label: 'Le RSVP en ligne, comment ça marche' },
  { id: 'outils', label: 'QR code, formulaire ou Google Form ?' },
  { id: 'date-limite', label: 'Quelle date limite de réponse ?' },
  { id: 'textes', label: 'Modèles de textes à copier' },
  { id: 'relancer', label: 'Relancer les invités sans réponse' },
  { id: 'faq', label: 'Questions fréquentes' },
];

export const STEPS = [
  {
    title: 'Vous partagez votre page',
    text: 'Un lien à envoyer par message ou par email, et un QR code à imprimer sur le faire-part.',
  },
  {
    title: 'Vos invités répondent en deux minutes',
    text: 'Sans créer de compte : ils cherchent leur nom, répondent événement par événement, et peuvent répondre pour toute leur famille.',
  },
  {
    title: 'Vous suivez tout au même endroit',
    text: 'Présents, absents, en attente : les compteurs se mettent à jour à chaque réponse, et votre liste s’exporte en Excel.',
  },
];

// Captures du parcours invité (assets/rsvp-*.webp)
export const SHOTS = [
  { key: 'invitation', caption: 'La page d’invitation, à vos couleurs' },
  { key: 'evenements', caption: 'Une réponse par événement' },
  { key: 'menu', caption: 'Le menu, les allergies, vos questions' },
  { key: 'confirmation', caption: 'Un récapitulatif, modifiable ensuite' },
];

export const FEATURES = [
  {
    title: 'La présence à chaque moment',
    text: 'Mairie, cérémonie, vin d’honneur, dîner, brunch : chacun répond pour ce à quoi il est invité.',
  },
  { title: 'Le choix du menu', text: 'Viande, poisson, végétarien : le traiteur a ses chiffres, table par table.' },
  {
    title: 'Allergies et questions',
    text: 'Régime alimentaire, navette, hébergement : ajoutez vos propres questions.',
  },
  {
    title: 'Toute la famille d’un coup',
    text: 'Un invité peut répondre pour son conjoint et ses enfants, sans rien ressaisir.',
  },
  { title: 'Une chanson, un petit mot', text: 'La musique qui les fera danser, et un message pour vous.' },
  {
    title: 'Une réponse modifiable',
    text: 'Un empêchement, un changement de menu ? Ils corrigent leur réponse eux-mêmes.',
  },
];

export const TEMPLATE_GROUPS: Array<{ id: string; title: string; intro: string; items: Template[] }> = [
  {
    id: 'faire-part',
    title: 'Sur le faire-part',
    intro: 'Une ligne en bas du faire-part, près du QR code.',
    items: [
      {
        title: 'Simple',
        text: 'Merci de nous confirmer votre présence avant le [date] sur [adresse du site], ou en scannant le QR code.',
      },
      { title: 'Formel', text: 'Réponse souhaitée avant le [date] : [adresse du site]' },
      {
        title: 'Chaleureux',
        text: 'Pour nous aider à tout préparer, dites-nous si vous serez des nôtres avant le [date], en quelques clics sur [adresse du site].',
      },
      { title: 'Bilingue', text: 'Merci de répondre avant le [date] · Kindly reply by [date] : [adresse du site]' },
    ],
  },
  {
    id: 'envoi',
    title: 'Pour envoyer le lien',
    intro: 'Par message, WhatsApp ou email, le jour où votre site est prêt.',
    items: [
      {
        title: 'À vos proches',
        text: 'Bonjour à tous ! Notre site de mariage est en ligne : [lien]. Vous y trouverez toutes les informations, et pourrez nous répondre en deux minutes avant le [date]. Nous avons hâte de vous retrouver ! [Vos prénoms]',
      },
      {
        title: 'Plus formel',
        text: 'Bonjour, nous avons la joie de vous inviter à notre mariage, le [date du mariage] à [lieu]. Vous trouverez toutes les informations et pourrez nous répondre ici : [lien]. Merci de nous répondre avant le [date]. [Vos prénoms]',
      },
    ],
  },
  {
    id: 'relances',
    title: 'Pour relancer',
    intro: 'Pour ceux qui n’ont pas encore répondu, à l’approche de la date limite.',
    items: [
      {
        title: 'Relance douce',
        text: 'Petit rappel : nous attendons encore votre réponse pour notre mariage du [date du mariage]. Vous pouvez répondre ici en deux minutes : [lien]. Merci ! [Vos prénoms]',
      },
      {
        title: 'Dernière relance',
        text: 'Nous donnons nos chiffres au traiteur le [date] : pourriez-vous nous dire d’ici là si vous serez présents ? Voici le lien : [lien]. Merci beaucoup ! [Vos prénoms]',
      },
    ],
  },
];

export const REMINDER_TIPS = [
  'Relancez une semaine avant la date limite, puis le lendemain pour les derniers : pas plus.',
  'Un message individuel marche mieux qu’un message groupé : chacun se sent attendu.',
  'Pour les grands-parents moins à l’aise avec internet, un coup de fil suffit : vous notez leur réponse vous-mêmes dans votre liste.',
  'Gardez la liste des invités sans réponse sous les yeux, pour savoir exactement qui relancer.',
];

export const FAQ: FaqItem[] = [
  {
    q: 'Que veut dire RSVP sur un faire-part ?',
    a: 'RSVP est l’abréviation de « Répondez s’il vous plaît ». Sur un faire-part, elle invite vos invités à confirmer leur présence, en général avant une date précise.',
  },
  {
    q: 'Quelle date limite de réponse indiquer ?',
    a: 'En général 4 à 6 semaines avant le mariage. Votre traiteur a besoin du nombre de couverts environ deux à trois semaines avant : gardez une marge pour relancer les retardataires.',
  },
  {
    q: 'Vos invités doivent-ils créer un compte pour répondre ?',
    a: 'Non. Ils ouvrent le lien ou scannent le QR code, cherchent leur nom dans la liste des invités et répondent. Rien à installer, aucun mot de passe.',
  },
  {
    q: 'Un invité peut-il répondre pour toute sa famille ?',
    a: 'Oui. Après avoir choisi son nom, il peut ajouter les personnes de son entourage invitées et répondre pour elles en même temps.',
  },
  {
    q: 'Et les invités qui ne sont pas à l’aise avec internet ?',
    a: 'Ils peuvent vous répondre par téléphone : vous enregistrez leur réponse vous-mêmes dans votre liste d’invités, et elle compte comme les autres.',
  },
  {
    q: 'Le RSVP en ligne est-il gratuit ?',
    a: 'Oui. RSVP Please est entièrement gratuit, sans publicité ni carte bancaire : site d’invitation, réponses, liste d’invités, plan de table, budget et rétroplanning.',
  },
];
